/**
 * Plant Component Palette
 *
 * Draggable component palette for adding plant equipment to the graph.
 * Components are organized by category and filtered by loop type.
 */

import { useState, useCallback, useMemo } from 'react';
import type { PlantComponentCategory, PlantComponentDefinition, PlantLoopType, PlantLoopSide } from './types';
import {
  PLANT_COMPONENTS,
  PLANT_CATEGORIES,
  PLANT_CATEGORY_NAMES,
  getPlantComponentsByLoopType,
} from './components';

interface PlantComponentPaletteProps {
  /** Current loop type for filtering */
  loopType?: PlantLoopType;
  /** Current loop side being edited */
  activeSide: PlantLoopSide;
  /** Callback when a component is dragged */
  onDragStart?: (componentType: string, loopSide: PlantLoopSide) => void;
}

/**
 * Get icon for palette
 */
function PaletteIcon({ icon, color }: { icon: string; color: string }) {
  const commonProps = {
    className: 'h-5 w-5',
    fill: color,
  };

  switch (icon) {
    case 'chiller':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <rect x="4" y="4" width="16" height="16" rx="2" fill="none" stroke={color} strokeWidth="2" />
          <circle cx="12" cy="12" r="4" fill={color} fillOpacity="0.3" />
        </svg>
      );
    case 'boiler':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <rect x="4" y="4" width="16" height="16" rx="2" fill="none" stroke={color} strokeWidth="2" />
          <path d="M12 8c-1 0-1.5 1-1.5 2s.5 1.5 1.5 2c1 .5 1.5 1 1.5 2s-.5 2-1.5 2" fill="none" stroke={color} strokeWidth="1.5" />
        </svg>
      );
    case 'tower':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <rect x="5" y="3" width="14" height="18" rx="1" fill="none" stroke={color} strokeWidth="2" />
          <path d="M8 7h8M8 11h8M8 15h8" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
        </svg>
      );
    case 'pump':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <circle cx="12" cy="12" r="7" fill="none" stroke={color} strokeWidth="2" />
          <path d="M12 7v5l3 1.5" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'splitter':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M4 12h6M10 12l5-5M10 12l5 5M15 7h5M15 17h5" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case 'mixer':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M4 7h5M4 17h5M9 7l5 5M9 17l5-5M14 12h6" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
        </svg>
      );
  }
}

/**
 * Single component item
 */
function ComponentItem({
  component,
  activeSide,
  onDragStart,
}: {
  component: PlantComponentDefinition;
  activeSide: PlantLoopSide;
  onDragStart: ((componentType: string, loopSide: PlantLoopSide) => void) | undefined;
}) {
  const handleDragStart = useCallback(
    (event: React.DragEvent) => {
      event.dataTransfer.setData('application/plantflow', component.type);
      event.dataTransfer.setData('loopSide', activeSide);
      event.dataTransfer.effectAllowed = 'move';
      onDragStart?.(component.type, activeSide);
    },
    [component.type, activeSide, onDragStart]
  );

  // Check if component is suitable for current side
  const isSuitableForSide =
    (activeSide === 'supply' && component.supplyEquipment) ||
    (activeSide === 'demand' && component.demandEquipment) ||
    component.category === 'distribution';

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className={`
        flex cursor-grab items-center gap-2 rounded-md border px-2 py-1.5 shadow-sm transition-all
        active:cursor-grabbing
        ${isSuitableForSide
          ? 'border-gray-200 bg-white hover:border-gray-300 hover:shadow'
          : 'border-gray-100 bg-gray-50 opacity-60 hover:opacity-80'
        }
      `}
      title={`${component.description}${!isSuitableForSide ? ' (Not typical for ' + activeSide + ' side)' : ''}`}
    >
      <div
        className="flex h-7 w-7 items-center justify-center rounded"
        style={{ backgroundColor: `${component.color}20` }}
      >
        <PaletteIcon icon={component.icon} color={component.color} />
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="truncate text-xs font-medium text-gray-700">{component.displayName}</div>
      </div>
      {!isSuitableForSide && (
        <span className="text-[10px] text-gray-400">*</span>
      )}
    </div>
  );
}

/**
 * Category section
 */
function CategorySection({
  category,
  components,
  expanded,
  onToggle,
  activeSide,
  onDragStart,
}: {
  category: PlantComponentCategory;
  components: PlantComponentDefinition[];
  expanded: boolean;
  onToggle: () => void;
  activeSide: PlantLoopSide;
  onDragStart: ((componentType: string, loopSide: PlantLoopSide) => void) | undefined;
}) {
  if (components.length === 0) return null;

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
      >
        <span className="text-sm font-medium text-gray-700">{PLANT_CATEGORY_NAMES[category]}</span>
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <span>{components.length}</span>
          <svg
            className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
          </svg>
        </span>
      </button>

      {expanded && (
        <div className="flex flex-col gap-1 px-2 pb-2">
          {components.map((component) => (
            <ComponentItem
              key={component.type}
              component={component}
              activeSide={activeSide}
              onDragStart={onDragStart}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Plant Component Palette
 */
export function PlantComponentPalette({
  loopType,
  activeSide,
  onDragStart,
}: PlantComponentPaletteProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<PlantComponentCategory>>(
    new Set(['pumps', 'chillers', 'boilers'])
  );

  const toggleCategory = useCallback((category: PlantComponentCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  // Filter components by loop type
  const availableComponents = useMemo(() => {
    if (loopType) {
      return getPlantComponentsByLoopType(loopType);
    }
    return PLANT_COMPONENTS;
  }, [loopType]);

  // Filter by search query
  const filteredComponents = useMemo(() => {
    if (!searchQuery.trim()) {
      return null;
    }

    const query = searchQuery.toLowerCase();
    return availableComponents.filter(
      (c) =>
        c.displayName.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query) ||
        c.idfClass.toLowerCase().includes(query)
    );
  }, [searchQuery, availableComponents]);

  return (
    <div className="flex h-full flex-col bg-gray-50">
      {/* Header */}
      <div className="border-b bg-white px-3 py-2">
        <h2 className="text-sm font-semibold text-gray-800">Plant Equipment</h2>
        <p className="text-xs text-gray-500">
          Drag to {activeSide === 'supply' ? 'supply' : 'demand'} side
        </p>
      </div>

      {/* Active side indicator */}
      <div className="border-b bg-white px-3 py-2">
        <div className="flex items-center gap-2">
          <div
            className="h-3 w-3 rounded-full"
            style={{
              backgroundColor: activeSide === 'supply' ? '#4CAF50' : '#FF9800',
            }}
          />
          <span className="text-xs font-medium text-gray-600">
            {activeSide === 'supply' ? 'Supply Side' : 'Demand Side'}
          </span>
        </div>
      </div>

      {/* Search */}
      <div className="border-b bg-white p-2">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); }}
            placeholder="Search equipment..."
            className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <svg
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Component list */}
      <div className="flex-1 overflow-y-auto">
        {filteredComponents ? (
          // Search results view
          <div className="p-2">
            {filteredComponents.length === 0 ? (
              <div className="py-4 text-center text-sm text-gray-500">
                No equipment found
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {filteredComponents.map((component) => (
                  <ComponentItem
                    key={component.type}
                    component={component}
                    activeSide={activeSide}
                    onDragStart={onDragStart}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          // Categorized view
          <div>
            {PLANT_CATEGORIES.map((category) => {
              const components = availableComponents.filter((c) => c.category === category);
              return (
                <CategorySection
                  key={category}
                  category={category}
                  components={components}
                  expanded={expandedCategories.has(category)}
                  onToggle={() => { toggleCategory(category); }}
                  activeSide={activeSide}
                  onDragStart={onDragStart}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="border-t bg-white px-3 py-2">
        <div className="text-xs text-gray-500">
          <span className="font-medium">Tip:</span> Connect outlets to inlets to form branches
        </div>
      </div>
    </div>
  );
}
